import useDeleteOperation from 'api/operation/common/useDeleteOperation';
import useInsertMovementOperation from 'api/operation/movement/useInsertMovementOperation';
import useMovementOperationList from 'api/operation/movement/useMovementOperationList';
import useUpdateMovementOperation from 'api/operation/movement/useUpdateMovementOperation';
import MovementOperationInsertModal from 'components/private/Modals/operation/movement/MovementOperationInsertModal/MovementOperationInsertModal';
import OperationEditModal from 'components/private/Modals/operation/OperationEditModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import PageSpinner from 'components/shared/pageSpinner/PageSpinner';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useMovementOperationTableColumns from 'hooks/operation/useMovementOperationTableColumns';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';

const MovementOperationList = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = useMemo(() => {
    return operationSettings?.defaultZone?.key;
  }, [operationSettings?.defaultZone?.key]);

  const movementPermissions =
    permissions.general.categories.warehouseOperations.movement;

  const canCreate = can(movementPermissions.create);
  const canDelete = can(movementPermissions.delete);
  const canDetail = can(movementPermissions.detail);
  const canEdit = can(movementPermissions.edit);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);
  const { getMovementOperationList, loading, movementOperationList, pageRef } =
    useMovementOperationList();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [deleteChosenMovementOperation, setDeleteChosenMovementOperation] =
    useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [movementOperationEditValues, setMovementOperationEditValues] =
    useState({});

  const { deleteRequest } = useDeleteOperation();
  const { updateRequest } = useUpdateMovementOperation();
  const { insertRequest } = useInsertMovementOperation();

  const tableRef = useRef(null);

  const fetchList = useCallback(
    ({ page: pageNumber = 1, count = pageSize, term = '' } = {}) => {
      if (!zoneGuid) return;
      getMovementOperationList({
        Guid: zoneGuid,
        Page: pageNumber,
        Count: count,
        Term: term,
      });
    },
    [getMovementOperationList, pageSize, zoneGuid],
  );

  useEffect(() => {
    if (!zoneGuid || !location.pathname.startsWith('/operation/movement'))
      return;
    fetchList({ page: 1, count: pageSize });
  }, []);

  useEffect(() => {
    fetchList({ page, count: pageSize });
  }, [pageSize]);

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenMovementOperation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const openItem = useCallback(
    row => () => {
      navigate(`/operation/movement-workspace/${row.guid}`);
    },
    [navigate],
  );

  const editItem = useCallback(
    row => () => {
      setMovementOperationEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const [columnsData] = useMovementOperationTableColumns({
    deleteItem,
    openItem,
    editItem,
    apiRef: tableRef,
    columnPermissions: {
      canDetail,
      canEdit,
      canDelete,
    },
  });

  const deleteHandle = guid => {
    setDeleteLoading(true);
    deleteRequest(
      guid,
      () => fetchList({ page, count: pageSize }),
      setIsDeleteModalOpen,
      setDeleteLoading,
    );
  };

  const editHandle = data => {
    if (!data) return;

    setEditLoading(true);
    updateRequest(
      {
        guid: movementOperationEditValues.guid,
        title: data.title,
      },
      () => fetchList({ page, count: pageSize }),
      setIsEditModalOpen,
      setEditLoading,
    );
  };

  const insertHandle = payload => {
    setInsertLoading(true);
    insertRequest(
      () => fetchList({ page, count: pageSize }),
      setIsInsertModalOpen,
      setInsertLoading,
      payload,
    );
  };

  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    fetchList({ page: pageNumber, count: pageSize });
  }

  function handleSearch(text) {
    setPage(1);
    fetchList({ page: 1, count: pageSize, term: text });
  }

  return (
    <RequireOperationSettings>
      <div>{pageRef.refTitle}</div>

      {loading ? (
        <PageSpinner loading />
      ) : (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={movementOperationList?.[page] ?? []}
          rows={movementOperationList?.[page] ?? []}
          pageSize={pageSize}
          isDeletable={canDelete}
          isLoading={loading}
          addLable={canCreate ? 'عملیات جابه‌جایی جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={setPageSize}
          apiRef={tableRef}
          onPageChange={getPageData}
          page={page}
          countPages={
            pageRef?.totalCount
              ? Math.ceil(pageRef.totalCount / Number(pageSize))
              : 1
          }
          handleSearchApi={handleSearch}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
        />
      )}

      {canDelete ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={closeDeleteModal}
          locationId={deleteChosenMovementOperation.guid}
          title={deleteChosenMovementOperation.title}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}

      {canEdit ? (
        <OperationEditModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={movementOperationEditValues}
        />
      ) : null}

      {canCreate ? (
        <MovementOperationInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
        />
      ) : null}
    </RequireOperationSettings>
  );
};

export default MovementOperationList;
